import React, { useCallback, useEffect, useMemo, useState } from 'react';

const readStoredCollapsed = (storageKey, fallback) => {
    if (!storageKey || typeof window === 'undefined') return fallback;

    try {
        const stored = window.localStorage.getItem(storageKey);

        if (stored === '1') return true;
        if (stored === '0') return false;
    } catch (error) {
        return fallback;
    }

    return fallback;
};

/**
 * Универсальный переключатель вида гранита.
 *
 * Не зависит от Redux, товара, цен или Three.js. Внешний код сам передаёт
 * подписи цены и размера, поэтому компонент можно использовать в админке,
 * публичном калькуляторе и любой отдельной 3D-модалке.
 */
const GraniteTextureRail = ({
    items = [],
    activeKey = '',
    disabled = false,
    orientation = 'vertical',
    appearance = 'vertical-strips',
    showLabels = true,
    ariaLabel = 'Выбор вида гранита',
    className = '',
    collapsible = false,
    collapsed,
    defaultCollapsed = false,
    storageKey = '',
    collapseLabel = 'Гранит',
    contextLabel = '',
    footerLabel = '',
    footerValue = '',
    onCollapsedChange,
    onChange,
    onManage,
}) => {
    const safeItems = useMemo(() => (
        Array.isArray(items) ? items.filter((item) => item?.key) : []
    ), [items]);
    const vertical = orientation !== 'horizontal';
    const controlledCollapsed = typeof collapsed === 'boolean';
    const [internalCollapsed, setInternalCollapsed] = useState(() => (
        readStoredCollapsed(storageKey, defaultCollapsed)
    ));
    const isCollapsed = collapsible && (controlledCollapsed ? collapsed : internalCollapsed);

    useEffect(() => {
        if (!storageKey || controlledCollapsed || typeof window === 'undefined') return;

        try {
            window.localStorage.setItem(storageKey, internalCollapsed ? '1' : '0');
        } catch (error) {
            // LocalStorage может быть отключён политикой браузера.
        }
    }, [controlledCollapsed, internalCollapsed, storageKey]);

    const setCollapsed = useCallback((nextValue) => {
        if (!controlledCollapsed) {
            setInternalCollapsed(nextValue);
        }

        onCollapsedChange?.(nextValue);
    }, [controlledCollapsed, onCollapsedChange]);

    const toggleCollapsed = useCallback(() => {
        setCollapsed(!isCollapsed);
    }, [isCollapsed, setCollapsed]);

    return (
        <aside
            className={[
                'graniteTextureRail',
                vertical ? 'graniteTextureRail--vertical' : 'graniteTextureRail--horizontal',
                showLabels ? 'graniteTextureRail--labeled' : 'graniteTextureRail--swatches',
                appearance === 'vertical-strips' ? 'graniteTextureRail--verticalStrips' : '',
                appearance === 'stacked-cards' ? 'graniteTextureRail--stackedCards' : '',
                isCollapsed ? 'is-collapsed' : 'is-expanded',
                className,
            ].filter(Boolean).join(' ')}
            aria-label={ariaLabel}
        >
            {collapsible ? (
                <button
                    type="button"
                    className="graniteTextureRail__collapseToggle"
                    onClick={toggleCollapsed}
                    disabled={disabled}
                    aria-expanded={!isCollapsed}
                    title={isCollapsed ? 'Развернуть выбор гранита' : 'Свернуть выбор гранита'}
                >
                    <span className="graniteTextureRail__collapseIcon" aria-hidden="true">
                        {isCollapsed ? '‹' : '›'}
                    </span>
                    <span className="graniteTextureRail__collapseLabel">{collapseLabel}</span>
                </button>
            ) : null}

            {!isCollapsed ? (
                <div className="graniteTextureRail__panel">
                    {contextLabel ? (
                        <div className="graniteTextureRail__context">
                            <span>Показываются цены</span>
                            <strong>{contextLabel}</strong>
                        </div>
                    ) : null}

                    <div className="graniteTextureRail__list">
                        {safeItems.map((item) => {
                            const key = String(item.key);
                            const label = String(item.label || item.name || key);
                            const textureUrl = String(item.textureUrl || '').trim();
                            const isActive = key === String(activeKey || '');
                            const itemDisabled = disabled || item.disabled === true;
                            const configured = item.configured !== false;
                            const priceLabel = String(item.priceLabel || '').trim();
                            const sizeLabel = String(item.sizeLabel || '').trim();
                            const titleParts = [label];

                            if (item.isDefault) titleParts.push('по умолчанию');
                            if (priceLabel) titleParts.push(priceLabel);
                            if (sizeLabel) titleParts.push(sizeLabel);
                            if (item.hint) titleParts.push(String(item.hint));

                            return (
                                <button
                                    type="button"
                                    key={key}
                                    className={[
                                        'graniteTextureRail__item',
                                        isActive ? 'is-active' : '',
                                        item.isDefault ? 'is-default' : '',
                                        item.muted ? 'is-muted' : '',
                                        configured ? 'is-configured' : 'is-unconfigured',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => onChange?.(key, item)}
                                    disabled={itemDisabled}
                                    title={titleParts.join(' · ')}
                                    aria-label={`Выбрать ${label}`}
                                    aria-pressed={isActive}
                                >
                                    <span
                                        className={`graniteTextureRail__texture ${textureUrl ? 'has-texture' : ''}`}
                                        style={textureUrl ? { backgroundImage: `url(${textureUrl})` } : undefined}
                                        aria-hidden="true"
                                    >
                                        {!textureUrl ? label.slice(0, 1).toUpperCase() : null}
                                    </span>

                                    {showLabels ? (
                                        <span className="graniteTextureRail__content">
                                            <strong>{label}</strong>
                                            <small className="graniteTextureRail__priceValue">
                                                {priceLabel || 'Не настроено'}
                                            </small>
                                            {sizeLabel ? (
                                                <small className="graniteTextureRail__sizeValue">
                                                    {sizeLabel}
                                                </small>
                                            ) : null}
                                            {item.isDefault ? <em>Основной</em> : null}
                                        </span>
                                    ) : null}

                                    <span
                                        className="graniteTextureRail__status"
                                        aria-hidden="true"
                                        title={configured ? 'Цена настроена' : 'Цена не настроена'}
                                    />

                                    {item.isDefault ? (
                                        <span className="graniteTextureRail__default" aria-hidden="true">★</span>
                                    ) : null}

                                    {isActive ? (
                                        <span className="graniteTextureRail__active" aria-hidden="true">✓</span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>

                    {(footerLabel || footerValue) ? (
                        <div className="graniteTextureRail__footer">
                            {footerLabel ? <span>{footerLabel}</span> : null}
                            {footerValue ? <strong>{footerValue}</strong> : null}
                        </div>
                    ) : null}

                    {onManage ? (
                        <button
                            type="button"
                            className="graniteTextureRail__manage"
                            onClick={onManage}
                            disabled={disabled}
                            title="Настроить виды гранита"
                            aria-label="Настроить виды гранита"
                        >
                            <span aria-hidden="true">⚙</span>
                            {showLabels ? <span>Настроить</span> : null}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </aside>
    );
};

export default React.memo(GraniteTextureRail);
